import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';

import p from 'theme/palette';
import SlateDialogButton from './SlateDialogButton';

// Track the "stable" scroll position of the #pages element. Uses a
// debounce so that an instantaneous scroll-then-click (e.g. browser
// automation scrolling an element into view) does not overwrite the
// value before the dialog has a chance to capture it.
var _stableScrollTop = 0;
var _scrollTimer = null;
var _scrollListenerBound = false;

function bindScrollTracker() {
  if (_scrollListenerBound) return true;
  var el = document.getElementById('pages');
  if (!el) return false;
  _scrollListenerBound = true;
  _stableScrollTop = el.scrollTop;
  el.addEventListener('scroll', function () {
    clearTimeout(_scrollTimer);
    _scrollTimer = setTimeout(function () {
      _stableScrollTop = el.scrollTop;
    }, 500);
  });
  return true;
}

// Bind the tracker as soon as #pages exists in the DOM, well before
// any dialog opens. This ensures the debounced position is accurate
// even for the very first dialog open in a session.
(function () {
  if (bindScrollTracker()) return;
  var attempts = 0;
  var interval = setInterval(function () {
    attempts++;
    if (bindScrollTracker() || attempts > 50) {
      clearInterval(interval);
    }
  }, 100);
})();

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: 'var(--sl-bg-surface)',
    border: '1px solid var(--sl-dialog-border)',
    '@media (max-width: 991px)': {
      margin: 16,
      maxHeight: 'calc(100% - 32px)',
      width: 'calc(100% - 32px)',
    },
  },
  paperMobileFixed: {
    '@media (max-width: 991px)': {
      height: 'calc(100% - 32px) !important',
      maxHeight: 'calc(100% - 32px) !important',
    },
  },
  paperFullScreen: {
    borderRadius: 0,
  },
  backdrop: {
    backgroundColor: 'rgba(8, 14, 24, 0.7)',
    backdropFilter: 'blur(4px)',
  },
  titleRoot: {
    padding: '18px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${p.dialogBorder}`,
    backgroundColor: 'var(--sl-bg-surface)',
  },
  title: {
    color: p.defaultText,
    fontSize: 18,
    fontWeight: 500,
    fontFamily: 'Montserrat, sans-serif',
    lineHeight: 1.3,
  },
  closeButton: {
    padding: 8,
    marginRight: -8,
    color: p.grey400,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: p.defaultText,
      backgroundColor: p.grey200,
    },
  },
  closeIcon: {
    fontSize: 18,
  },
  content: {
    padding: '8px 24px 24px',
  },
  contentNoPadding: {
    padding: 0,
  },
  actions: {
    height: 65,
    boxSizing: 'border-box',
    padding: '0 24px',
    borderTop: `1px solid ${p.dialogBorder}`,
    gap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    '@media (max-width: 991px)': {
      padding: '0 16px',
      '& > *': {
        flex: 1,
      },
    },
  },
}));

const SlateDialog = ({
  open,
  onClose,
  title,
  closable = true,
  actions = [],
  children,
  maxWidth = 'sm',
  fullWidth = true,
  contentPadding = true,
  mobileFixedHeight = false,
  ...rest
}) => {
  const classes = useStyles();

  // Restore the container scroll position when the dialog mounts
  // and unmounts. _stableScrollTop holds the last position before
  // any rapid scroll-then-click sequence (debounced), so
  // automation-caused scroll shifts are not captured. The mount
  // restore runs in useLayoutEffect (before paint) so the user
  // never sees the shift. The unmount restore is deferred with
  // setTimeout so it runs after MUI's own scroll-lock cleanup.
  React.useLayoutEffect(function () {
    bindScrollTracker();
    var savedScrollTop = _stableScrollTop;
    var el = document.getElementById('pages');
    if (el) {
      el.scrollTop = savedScrollTop;
    }
    return function () {
      setTimeout(function () {
        var el = document.getElementById('pages');
        if (el) {
          el.scrollTop = savedScrollTop;
        }
      }, 50);
    };
  }, []);

  const handleClose = (event, reason) => {
    if (!closable && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="slate-dialog-title"
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      disableEscapeKeyDown={!closable}
      PaperProps={{
        className: [
          classes.paper,
          rest.fullScreen ? classes.paperFullScreen : '',
          mobileFixedHeight ? classes.paperMobileFixed : '',
        ].filter(Boolean).join(' '),
      }}
      BackdropProps={{
        className: classes.backdrop,
      }}
      {...rest}
    >
      <DialogTitle id="slate-dialog-title" disableTypography className={classes.titleRoot}>
        <Typography className={classes.title}>{title}</Typography>
        {closable && onClose && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
            size="small"
          >
            <i className={'material-symbols-outlined ' + classes.closeIcon}>close</i>
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent className={contentPadding ? classes.content : classes.contentNoPadding}>
        {children}
      </DialogContent>

      {actions.length > 0 && (
        <DialogActions className={classes.actions}>
          {actions.map((action, index) => (
            <SlateDialogButton
              key={action.key || index}
              variant={action.variant || 'secondary'}
              onClick={action.onClick}
              disabled={action.disabled}
              loading={action.loading}
            >
              {action.label}
            </SlateDialogButton>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

SlateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  closable: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['primary', 'secondary', 'destructive']),
      disabled: PropTypes.bool,
      loading: PropTypes.bool,
      key: PropTypes.string,
    })
  ),
  children: PropTypes.node,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
  contentPadding: PropTypes.bool,
  mobileFixedHeight: PropTypes.bool,
};

export default SlateDialog;
